import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { RESOURCES, USERMENU } from '@app/data/_index';
import {
  FilterMatchType,
  HeaderMenu,
  Learning_Type,
  ResourcesMenu,
  Role,
  SessionStorageKeys,
  SubscriptionType,
  Topic,
} from '@app/enums/_index';
import {
  LearningPathFactory,
  LearningTypeFactory,
  RoleFactory,
  SubscriptionFactory,
  TopicFactory,
} from '@app/factories/_index';
import {
  Certificate,
  LMSSubscription,
  LearningPath,
  LearningType,
  ResourcesMenuItem,
  Roles,
  Topics,
  User,
  UserMenuItem,
} from '@app/models/_index';
import { CertificationService, GoogleAnalyticsService, UserService } from '@app/services/_index';
import {
  calculateExpirationDate,
  logGoogleAnalytics,
  removeData,
  removeStorageItemsByKey,
} from '@app/utilities/_index';
import { Observable, Subscription } from 'rxjs';

import { Router } from '@angular/router';
import { RoutesType } from '@app/app-routing.module';
import { HasAnalytics } from '@app/interfaces/_index';
import { environment } from '@environments/environment';
import { UserClaims } from '@okta/okta-auth-js';

@Component({
  selector: 'app-header-mega-menu',
  templateUrl: './header-mega-menu.component.html',
  styleUrls: ['./header-mega-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class HeaderMegaMenuComponent
  implements OnInit, OnDestroy, HasAnalytics, AfterViewChecked, AfterViewInit
{
  sub: Subscription;
  mydropdown = false;
  showMegaMenuResources: boolean;
  isExpandedProducts = false;
  isExpandedCatalog = false;
  isExpandedResources = false;
  isExpandedDashboard = false;
  isOpenType = true;
  isOpenTopic = false;
  isOpenRole = false;
  isOpenLearningPath = false;
  isOpenTrainingSupport = false;
  isOpenCertifications = false;
  isOpenVideos = false;
  isOpenTermsAndConditions = false;

  currentUser: User;
  claims$: Observable<UserClaims>;
  doceboUrl = environment.docebo.url;
  resourceArray = RESOURCES.resource; // Read in navigation resources as json array from resources.json file
  userMenuArray = USERMENU.usermenu; // Read in navigation usermenu as json array from usermenu.json file
  navBarRoutes: RoutesType[keyof RoutesType][];

  subscriptionItems: LMSSubscription[] = [];
  learningTypeItems: LearningType[] = [];
  topicItems: Topics[] = [];
  roleItems: Roles[] = [];
  learningPathItems: LearningPath[] = [];
  certificateArray: Certificate[] = [];
  certificateExpiryArray: Certificate[] = [];

  inboxCount: number = 0; // Message inbox count
  headerHeight: number;
  query: string; // search params query
  @ViewChild('headerContentHeight', { static: true }) elementView: ElementRef;
  constructor(
    public router: Router,
    private userService: UserService,
    private googleAnalyticsService: GoogleAnalyticsService,
    public cdRef: ChangeDetectorRef,
    public certificateService: CertificationService,
    public subscriptionFactory: SubscriptionFactory,
    public learningPathFactory: LearningPathFactory,
    public learningTypeFactory: LearningTypeFactory,
    public topicFactory: TopicFactory,
    public roleFactory: RoleFactory
  ) {}

  @HostListener('click', ['$event']) click(e) {
    e.stopPropagation();
  }

  @HostListener('document:click') resetToggle() {
    this.isExpandedResources = false;
    this.isExpandedCatalog = false;
    this.isExpandedProducts = false;
    this.isExpandedDashboard = false;
  }

  async ngOnInit(): Promise<void> {
    this.resourceArray.forEach((resource) => {
      if (resource.name === ResourcesMenu.VOUCHER_REDEMPTION) {
        resource.display = environment.beyond.featureFlags.showVoucher;
      }
    });

    this.showMegaMenuResources = environment.beyond.featureFlags.showMegaMenuResources;
    this.subscriptionItems = this.subscriptionFactory.getCollection();
    this.learningTypeItems = this.learningTypeFactory.getCollection();
    this.topicItems = this.topicFactory.getCollection();
    this.roleItems = this.roleFactory.getCollection();
    this.learningPathItems = this.learningPathFactory.getCollection();

    // NOTE: Remember to unsubscribe user to prevent memory leaks
    this.claims$ = this.userService.claims$;

    // Get the expiring certificates count to update inbox count on the user avatar and
    // Check local storage for certificates
    const certificates = JSON.parse(localStorage.getItem(SessionStorageKeys.CERTIFICATES)) || [];
    this.certificateArray = certificates;
    this.getCertificationExpiryNotification(this.certificateArray);
    if (this.certificateExpiryArray.length > 0) {
      this.inboxCount = this.certificateExpiryArray.length;
    }
  }

  private googleTranslateInitialized = false;

  private isKarma(): boolean {
    return typeof (window as any).__karma__ !== 'undefined';
  }

  ngAfterViewInit(): void {
    if (this.isKarma()) {
      return;
    }
    if (this.googleTranslateInitialized) {
      return;
    }
    this.googleTranslateInitialized = true;
    this.cleanAndInitGoogleTranslateWidget();
  }
  /**
   * Calculating the height of a header div and setting them up to fix the Maintnance notification
   */
  ngAfterViewChecked(): void {
    this.headerHeight = this.elementView.nativeElement.offsetHeight;
    this.cdRef.detectChanges();
  }

  /**
   * OnDestroy unsubscribe from auth service user
   */
  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  /**
   * Redirects user to registration url
   */
  sendToRegistration(): void {
    window.location.href = environment.beyondRegistration.url + '/education-registration';
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * On Catalog Search triggered on click or enter
   * @param query - search query coming form search input form emitter
   */
  onSearch(query: string): void {
    this.onChangeQuery(query);

    // Send Google Analytics event
    this.sendGoogleAnalytics('Catalog', 'search', query);

    // Send query parameter to the search page to be processed for search
    this.router.navigate(['/search', { s: query }]);
  }

  /**
   * Pull the Query data
   * @param query returns query
   */
  onChangeQuery(query): void {
    this.query = query;
  }

  /**
   * Clear Session Storage keys
   */
  clearStorage(): void {
    removeStorageItemsByKey(sessionStorage, SessionStorageKeys.ACCORDION);
    removeData(sessionStorage, SessionStorageKeys.FILTERED_SEARCH_RESULTS);
  }

  /**
   * On Toggle Header Menu for mobile navigation
   */
  onToggleDropdown(): void {
    this.mydropdown = !this.mydropdown;
  }

  /**
   * On Logout Click
   * Redirect to home page after log out
   */
  onLogoutClick(): void {
    // Redirect to home page
    const currentUrl = this.router.url;
    this.userService.logoutAndRedirectTo(currentUrl);
  }

  /**
   * Returns expired certifications that have expired after specific time range.
   * Expiring certificates will show if they are within 60 days or 2 months. Most
   * certificates will expire in years, so there maybe no expired certifiates that may show for years.
   * @returns Array of certicates that have expired
   */
  getCertificationExpiryNotification(certificateArray: Certificate[]): void {
    // Check certificates if they have expired and flag them as expired
    if (certificateArray && certificateArray.length > 0) {
      certificateArray.forEach((certificateItem: Certificate) => {
        const diffDays = calculateExpirationDate(certificateItem.expires);
        if (diffDays < 60 && diffDays >= 0) {
          certificateItem.expiredFlag = true;
          certificateItem.expiryDueDate = diffDays;
          // Push expired arrays
          this.certificateExpiryArray.push(certificateItem);
        } else {
          certificateItem.expiredFlag = false;
        }
      });
    }
  }

  /**
   * Filters Catalog by Topic
   * @param topic - topic as query params
   */
  filteredTopicCatalog(topic: string): void {
    const queryParams = this.getSearchQueryByTopic(topic);
    this.router.navigate(['search', queryParams ? queryParams : {}]);
    this.isExpandedCatalog = false;
  }

  /**
   * Filters Catalog by Learning Type
   * @param learning_type - learning_type as query params
   */
  filteredLearningTypeCatalog(learningType: string): void {
    const queryParams = this.getSearchQueryByLearningType(learningType);
    this.router.navigate(['search', queryParams ? queryParams : {}]);
    this.isExpandedCatalog = false;
  }

  /**
   * Filters Catalog by Role
   * @param role - role as query params
   */
  filteredRoleCatalog(role: string): void {
    if (role === Role.LEARNING_PATHS_BY_ROLE) {
      this.router.navigateByUrl('learning-path/home');
      this.isExpandedCatalog = false;
    } else {
      const queryParams = this.getSearchQueryByRole(role);
      this.router.navigate(['search', queryParams ? queryParams : {}]);
      this.isExpandedCatalog = false;
    }
  }

  /**
   * Reinitializes the Google Translate widget by removing any previously injected
   * initialization and loader scripts, then injecting fresh scripts that register
   * `googleTranslateElementInit()` and load Google's translate runtime.
   *
   * This avoids duplicate widget instances and inconsistent behavior when the header
   * re renders or when the widget needs to be mounted into a different DOM target.
   *
   * @param targetId DOM element id where the TranslateElement should be rendered
   */

  cleanAndInitGoogleTranslateWidget(targetId: string = 'google_translate_element'): void {
    const w = window as any;

    // Do not load external scripts during unit tests
    if (typeof w.__karma__ !== 'undefined') {
      return;
    }

    const prototypeId = 'gt-prototype';
    const invokeId = 'gt-invoke';

    // If the translate loader already exists, do not inject again
    if (document.getElementById(invokeId)) {
      return;
    }

    // Remove any previously injected scripts
    document.getElementById(prototypeId)?.remove();
    document.getElementById(invokeId)?.remove();

    // Clear target container to avoid duplicate widget markup
    const targetEl = document.getElementById(targetId);
    if (targetEl) {
      targetEl.innerHTML = '';
    }

    const languages = ['en', 'es', 'pt', 'ja', 'fr'].join(',');

    // Define the global callback directly rather than via inline script
    w.googleTranslateElementInit = () => {
      const google = (window as any).google;
      if (!google?.translate?.TranslateElement) {
        return;
      }

      new google.translate.TranslateElement(
        {
          pageLanguage: 'en',
          includedLanguages: languages,
          layout: google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        targetId
      );
    };

    // Inject the loader script
    const s = document.createElement('script');
    s.id = invokeId;
    s.type = 'text/javascript';
    s.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    s.async = true;
    s.defer = true;

    // If the load fails, allow future retries
    s.onerror = () => {
      document.getElementById(invokeId)?.remove();
      delete w.googleTranslateElementInit;
    };

    document.body.appendChild(s);
  }

  /**
   * Return query string to be passed to the search route to filter the catalog
   * by role
   * role = search or product to search
   * @returns search query string based on role
   */
  getSearchQueryByRole(role: string): object {
    if (role) {
      return { role: role };
    } else {
      return {};
    }
  }

  /**
   * Return query string to be passed to the search route to filter the catalog
   * by learning_type
   * learning_type = search or product to search
   * @returns search query string based on learning_type
   */
  getSearchQueryByLearningType(learning_type: string): object {
    if (learning_type) {
      return { type: learning_type };
    } else {
      return {};
    }
  }

  /**
   * Return query string to be passed to the search route to filter the catalog
   * by topic
   * topic = search or product to search
   * @returns search query string based on topic
   */
  getSearchQueryByTopic(topic: string): object {
    if (topic) {
      return { topic: topic };
    } else {
      return {};
    }
  }

  /**
   * Toggle arrow for header menu
   * @param menu - menu name for toggle
   */
  toggleMenu(menu: string, e?: Event): void {
    if (e) {
      e.stopPropagation();
    }
    switch (menu) {
      case HeaderMenu.PRODUCTS:
        this.isExpandedProducts = !this.isExpandedProducts;
        this.isExpandedCatalog = false;
        this.isExpandedResources = false;
        this.isExpandedDashboard = false;
        break;
      case HeaderMenu.CATALOG:
        this.isExpandedCatalog = !this.isExpandedCatalog;
        this.isExpandedProducts = false;
        this.isExpandedResources = false;
        this.isExpandedDashboard = false;
        break;
      case HeaderMenu.RESOURCES:
        this.isExpandedResources = !this.isExpandedResources;
        this.isExpandedProducts = false;
        this.isExpandedCatalog = false;
        this.isExpandedDashboard = false;
        break;
      case HeaderMenu.MY_DASHBOARD:
        this.isExpandedDashboard = !this.isExpandedDashboard;
        this.isExpandedProducts = false;
        this.isExpandedCatalog = false;
        this.isExpandedResources = false;
        break;
      default:
        break;
    }
  }

  /**
   * Toggle arrow for header menu
   * @param menu - menu name for toggle
   */
  toggleCatalog(menu: string): void {
    switch (menu) {
      case FilterMatchType.INSTRUCTION_TYPE:
        this.isOpenType = true;
        this.isOpenTopic = false;
        this.isOpenRole = false;
        break;
      case FilterMatchType.TOPIC:
        this.isOpenTopic = true;
        this.isOpenType = false;
        this.isOpenRole = false;
        break;
      case FilterMatchType.ROLE:
        this.isOpenRole = true;
        this.isOpenType = false;
        this.isOpenTopic = false;
        break;
      default:
        break;
    }
  }

  /**
   * Toggle arrow for header menu
   * @param menu - menu name for toggle
   */
  toggleResources(menu: string): void {
    switch (menu) {
      case ResourcesMenu.GETTING_STARTED:
        this.isOpenTrainingSupport = true;
        this.isOpenLearningPath = false;
        this.isOpenCertifications = false;
        this.isOpenVideos = false;
        this.isOpenTermsAndConditions = false;
        break;
      case ResourcesMenu.LEARNING_PATHS:
        this.isOpenLearningPath = true;
        this.isOpenTrainingSupport = false;
        this.isOpenCertifications = false;
        this.isOpenVideos = false;
        this.isOpenTermsAndConditions = false;
        break;
      case ResourcesMenu.CERTIFICATIONS:
        this.isOpenCertifications = true;
        this.isOpenTrainingSupport = false;
        this.isOpenLearningPath = false;
        this.isOpenVideos = false;
        this.isOpenTermsAndConditions = false;
        break;
      case ResourcesMenu.VIDEOS:
        this.isOpenCertifications = false;
        this.isOpenTrainingSupport = false;
        this.isOpenLearningPath = false;
        this.isOpenVideos = true;
        this.isOpenTermsAndConditions = false;
        break;
      case ResourcesMenu.TERMS_AND_CONDITION:
        this.isOpenCertifications = false;
        this.isOpenTrainingSupport = false;
        this.isOpenLearningPath = false;
        this.isOpenVideos = false;
        this.isOpenTermsAndConditions = true;
        break;
      default:
        break;
    }
  }

  /**
   * Add Track By Resource Name to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of resource array
   * @param item - Resource item
   * @returns resource name
   */
  trackByResourceName(index: number, item: ResourcesMenuItem): string {
    if (item) {
      return item.name;
    } else {
      return String(index);
    }
  }

  /**
   * Add Track By UserMenu Name to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of resource array
   * @param item - UserMenu item
   * @returns UserMenu name
   */
  trackByUserMenu(index: number, item: UserMenuItem): string {
    if (item) {
      return item.name;
    } else {
      return String(index);
    }
  }

  /**
   * Add Track By Subscription Item to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Subscription Item array
   * @param item - LMS Subscription item
   * @returns LMS Subscription id
   */
  trackBySubscriptionItem(index: number, item: LMSSubscription): number {
    if (item) {
      return item.id;
    } else {
      return index;
    }
  }

  /**
   * Add Track By Learning Type Item to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Learning Type Item array
   * @param item - Topics item
   * @returns Item value of Learning Type array
   */
  trackByLearningTypeItem(index: number, item: LearningType): string {
    if (item) {
      return item.value;
    } else {
      return String(index);
    }
  }

  /**
   * Add Track By Topics Item to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Topic Item array
   * @param item - Topics item
   * @returns Item value of Topics array
   */
  trackByTopicItem(index: number, item: Topics): string {
    if (item) {
      return item.value;
    } else {
      return String(index);
    }
  }

  /**
   * Add Track By Role Item to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Role Item array
   * @param item - Role item
   * @returns Item value of Role array
   */
  trackByRoleItem(index: number, item: Roles): string {
    if (item) {
      return item.value;
    } else {
      return String(index);
    }
  }

  /**
   * Add Track By Learning Path Item to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Learning Path Item array
   * @param item - Learning Path item
   * @returns Item value of Learning Path array
   */
  trackByLearningPathItem(index: number, item: LearningPath): string {
    if (item) {
      return item.value;
    } else {
      return String(index);
    }
  }

  /**
   * Subscription Type enums based on subscription types
   */
  public get subscriptionType(): typeof SubscriptionType {
    return SubscriptionType;
  }

  /**
   * Subscription Type enums based on Roles
   */
  public get role(): typeof Role {
    return Role;
  }

  /**
   * Subscription Type enums based on Topics
   */
  public get topic(): typeof Topic {
    return Topic;
  }

  /**
   * Subscription Type enums based on Learning_Type
   */
  public get learning_type(): typeof Learning_Type {
    return Learning_Type;
  }

  /**
   *  HeaderMenu enums added.
   */
  public get headerMenu(): typeof HeaderMenu {
    return HeaderMenu;
  }

  /**
   *  HeaderMenu enums added.
   */
  public get resourcesMenu(): typeof ResourcesMenu {
    return ResourcesMenu;
  }

  /**
   *  HeaderMenu enums added.
   */
  public get filterMatchType(): typeof FilterMatchType {
    return FilterMatchType;
  }
}
